import React, { FC, useEffect, useState } from 'react'
import {
  NarrationModel,
  selectIsNarrationIdInProgress,
} from '@store/slices/narration.slice'
import { AudioSourceType } from '@shared/enums'
import { AppSelectComponent } from '@components/shared/appSelect.component'
import { ButtonGroup, ToggleButton } from 'react-bootstrap'
import AppButton from '@components/shared/appButton'
import { FastForward, MusicNoteBeamed, Tools } from 'react-bootstrap-icons'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchAllVoices,
  selectAllVoices,
  selectVoiceLoading,
} from '@store/slices/voice.slice'
import { AppDispatch, RootState } from '@store/store'
import {
  handleGenerateAudioForNarration,
  isNarrationUpdated,
} from '@services/narration.service'
import {
  generateSsml,
  updateNarration,
  updateNarrationsVoice,
} from '@store/thunk/narration.thunk'
import { useParams } from 'react-router-dom'
import { getAudioSourceType } from '@shared/tools/enum.tools'
import { selectAllTemplates } from '@store/slices/template.slice'
import { AISlotModel, selectAiSlotById } from '@store/slices/aiSlot.slice'
import { toDateHourString } from '@shared/tools/time.tools'
import AppAudioPlayerComponent from '@components/shared/appAudioPlayer.component'
import { selectNarrationBySlotAndRegion } from '@store/selectors/narration.selectors'
import TextEditorComponent from '@components/shared/textEditor.component'
import SsmlEditorComponent from '@components/shared/ssmlEditor.component'
import DialogueEditorComponent from '@components/shared/dialogueEditor.component'
import { formatXml } from '@shared/tools/ssml.tools'
import { useNarrationFormContext } from '@components/narration/narration.component'

const NarrationFormComponent: FC = () => {
  const { source } = useNarrationFormContext()

  return (
    <div className={'w-100'}>
      <NarrationAudio />
      <VoiceSelect />
      <SourceActions />
      {(source == AudioSourceType.PHONETIC ||
        source == AudioSourceType.TEXT) && <TextEditorComponent />}
      {source == AudioSourceType.SSML && <SsmlEditorComponent />}
      {source == AudioSourceType.DIALOGUE && <DialogueEditorComponent />}
    </div>
  )
}

const SourceActions: FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const templates = useSelector(selectAllTemplates)
  const { dialogNumber, setSource, isSaveDisabled } = useNarrationFormContext()
  const { regionId, slotId, sourceType, playlistId } = useParams()
  const source = sourceType ? getAudioSourceType(sourceType) : undefined
  const currentNarration: NarrationModel | undefined = useSelector(
    (state: RootState) =>
      slotId && regionId
        ? selectNarrationBySlotAndRegion(state, {
            slotId,
            regionId,
          })
        : undefined
  )
  const aiSlot: AISlotModel | null = useSelector((state: RootState) =>
    slotId ? selectAiSlotById(state, slotId) : null
  )
  const isNarrationLoading = useSelector((state: RootState) =>
    currentNarration
      ? selectIsNarrationIdInProgress(state, currentNarration.id)
      : false
  )
  const narrationLoading = useSelector((state: RootState) =>
    currentNarration
      ? selectIsNarrationIdInProgress(state, currentNarration.id)
      : null
  )

  const isGenerateSsmlDisabled = (): boolean => {
    const template = templates.find(
      (template) => template.id == aiSlot?.template
    )
    return (
      !isSaveDisabled() ||
      ((source == AudioSourceType.TEXT || source == AudioSourceType.PHONETIC) &&
        (!template || (template.bgAudioSelect && !aiSlot?.bgAudio)))
    )
  }

  const onGenerateSsml = () => {
    if (currentNarration && source && source != AudioSourceType.SSML)
      return dispatch(
        generateSsml({
          narrationId: currentNarration.id,
          source: source,
          dialogueNumber: dialogNumber,
        })
      )
        .then(unwrapResult)
        .then(() => {
          setSource(AudioSourceType.SSML)
        })
  }

  const isGenerateAudioDisabled = (): boolean => {
    return (
      !source ||
      !isSaveDisabled() ||
      (source != AudioSourceType.SSML && isGenerateSsmlDisabled()) ||
      isNarrationLoading ||
      !regionId ||
      (currentNarration?.text == '' && source === AudioSourceType.TEXT) ||
      (currentNarration?.phonetic === '' &&
        !!source &&
        source === AudioSourceType.PHONETIC) ||
      (currentNarration?.ssml == '' &&
        !!source &&
        source === AudioSourceType.SSML)
    )
  }

  const onGenerateAudioClick = () => {
    if (currentNarration && playlistId && source)
      return handleGenerateAudioForNarration(
        currentNarration,
        playlistId,
        source,
        dialogNumber
      )
  }

  const onFormatSsmlClick = () => {
    if (currentNarration) {
      const formatedSsml = formatXml(currentNarration?.ssml)
      const updatedNarration = {
        ...currentNarration,
        ssml: formatedSsml,
      }
      return dispatch(
        updateNarration({
          narrationId: currentNarration.id,
          ...updatedNarration,
        })
      )
    }
  }

  return (
    <div className={'d-flex gap-2 my-2'}>
      <ButtonGroup>
        <ToggleButton
          id={'text'}
          type="radio"
          variant="secondary"
          value={'text'}
          checked={source && source == AudioSourceType.TEXT}
          onChange={() => setSource(AudioSourceType.TEXT)}
        >
          Text
        </ToggleButton>
        {currentNarration && currentNarration.phonetic && (
          <ToggleButton
            id={'phonetic'}
            type="radio"
            variant="secondary"
            value={'phonetic'}
            checked={!!source && source == AudioSourceType.PHONETIC}
            onChange={() => setSource(AudioSourceType.PHONETIC)}
          >
            Fonetický přepis
          </ToggleButton>
        )}

        {currentNarration && currentNarration.dialogue && (
          <ToggleButton
            id={'dialogue'}
            type="radio"
            variant="secondary"
            value={'dialogue'}
            checked={!!source && source == AudioSourceType.DIALOGUE}
            onChange={() => setSource(AudioSourceType.DIALOGUE)}
          >
            Dialog
          </ToggleButton>
        )}
        <AppButton
          onClick={() => onGenerateSsml()}
          buttonChild={<FastForward size={24} />}
          isLoading={false}
          disabled={
            !source ||
            source == AudioSourceType.SSML ||
            isGenerateSsmlDisabled()
          }
          buttonVariant={'primary'}
          tooltip={'Vygenerovat SSML'}
        />
        <ToggleButton
          id={'ssml'}
          type="radio"
          variant="secondary"
          value={'ssml'}
          checked={!!source && source === AudioSourceType.SSML}
          onChange={() => setSource(AudioSourceType.SSML)}
          className={'checked'}
        >
          SSML
        </ToggleButton>
      </ButtonGroup>
      <AppButton
        isLoading={narrationLoading}
        buttonVariant={'primary'}
        tooltip={'Generovat audio'}
        buttonChild={<MusicNoteBeamed />}
        onClick={onGenerateAudioClick}
        disabled={isGenerateAudioDisabled()}
      ></AppButton>
      {source == AudioSourceType.SSML && (
        <AppButton
          isLoading={false}
          buttonVariant={'primary'}
          tooltip={'Přeformátovat SSML'}
          buttonChild={<Tools />}
          onClick={onFormatSsmlClick}
        ></AppButton>
      )}
    </div>
  )
}

const NarrationAudio: FC = () => {
  const { regionId, slotId } = useParams()
  const currentNarration: NarrationModel | undefined = useSelector(
    (state: RootState) =>
      slotId && regionId
        ? selectNarrationBySlotAndRegion(state, {
            slotId,
            regionId,
          })
        : undefined
  )
  return (
    <div>
      <div
        className={'d-flex flex-row gap-2 justify-content-end mb-1'}
        style={{ fontSize: '0.9rem' }}
      >
        {currentNarration?.audioCreated && (
          <span>
            aktualizováno:{' '}
            <strong
              className={
                isNarrationUpdated(currentNarration) ? 'text-primary' : ''
              }
            >
              {toDateHourString(currentNarration.audioCreated)}
            </strong>
          </span>
        )}
        {currentNarration?.uploaded && (
          <span>
            publikováno:{' '}
            <strong
              className={
                isNarrationUpdated(currentNarration) ? '' : 'text-success'
              }
            >
              {toDateHourString(currentNarration.uploaded)}
            </strong>
          </span>
        )}
      </div>
      <div className={'mb-2'} style={{ minHeight: '52px' }}>
        <AppAudioPlayerComponent
          audioSrc={currentNarration?.audioUrl}
        ></AppAudioPlayerComponent>
      </div>
    </div>
  )
}

const VoiceSelect: FC = () => {
  const { slotId, sourceType, regionId } = useParams()
  const source = sourceType ? getAudioSourceType(sourceType) : undefined
  const dispatch = useDispatch<AppDispatch>()
  const voices = useSelector(selectAllVoices)
  const voiceLoading = useSelector(selectVoiceLoading)
  const [selectedVoice, setSelectedVoice] = useState<EntityId>()
  const [selectedDialogFirstPirsonVoice, setSelectedDialogFirstPirsonVoice] =
    useState<EntityId>()
  const [selectedDialogSecondPirsonVoice, setSelectedDialogSecondPirsonVoice] =
    useState<EntityId>()
  const aiSlot: AISlotModel | null = useSelector((state: RootState) =>
    slotId ? selectAiSlotById(state, slotId) : null
  )
  const voiceOptions = voices.map((voice) => ({
    id: voice.id,
    label: voice.name,
  }))
  const currentNarration: NarrationModel | undefined = useSelector(
    (state: RootState) =>
      slotId && regionId
        ? selectNarrationBySlotAndRegion(state, {
            slotId,
            regionId,
          })
        : undefined
  )

  //Fetch voices if not present
  useEffect(() => {
    if (voices?.length == 0 && !voiceLoading) {
      dispatch(fetchAllVoices())
    }
    if (voices) {
      setSelectedDialogFirstPirsonVoice(
        voices.find((voice) => voice.isDialogueFirstPersonDefault)?.id
      )
      setSelectedDialogSecondPirsonVoice(
        voices.find((voice) => voice.isDialogueSecondPersonDefault)?.id
      )
    }
  }, [voices])

  //Set voices
  useEffect(() => {
    if (currentNarration) {
      setSelectedVoice(currentNarration.voice)
    }
  }, [currentNarration])

  const onNarrationVoiceChange = (voiceId: EntityId | null) => {
    if (voiceId && currentNarration) {
      dispatch(
        updateNarrationsVoice({
          narrationId: currentNarration.id,
          voice: voiceId,
        })
      )
    } else {
      toast.warning('Hlas musí být vybrána')
    }
  }

  const onSelectFirstVoiceDialogueChange = (voiceId: EntityId | null) => {
    if (voiceId && aiSlot) {
      toast.warning('Not implemented yet')
    } else {
      toast.warning('Hlas musí být vybrána')
    }
  }

  const onSelectSecondVoiceDialogueChange = (voiceId: EntityId | null) => {
    if (voiceId && aiSlot) {
      toast.warning('Not implemented yet')
    } else {
      toast.warning('Hlas musí být vybrána')
    }
  }

  return (
    <div className={'d-flex gap-2 align-items-center'}>
      Hlas&emsp;
      {source != AudioSourceType.DIALOGUE && source != AudioSourceType.SSML && (
        <AppSelectComponent
          onSelectChange={onNarrationVoiceChange}
          options={voiceOptions}
          valueId={selectedVoice}
          placeholder={'Vyberte hlas'}
          className={'p-0'}
        ></AppSelectComponent>
      )}
      {source == AudioSourceType.DIALOGUE && (
        <>
          <AppSelectComponent
            onSelectChange={onSelectFirstVoiceDialogueChange}
            options={voiceOptions}
            isDisabled={true}
            valueId={selectedDialogFirstPirsonVoice}
            placeholder={'Vyberte první hlas dialogu'}
            className={'p-0'}
          ></AppSelectComponent>
          <AppSelectComponent
            onSelectChange={onSelectSecondVoiceDialogueChange}
            options={voiceOptions}
            isDisabled={true}
            valueId={selectedDialogSecondPirsonVoice}
            placeholder={'Vyberte druhý hlas dialogu'}
            className={'p-0'}
          ></AppSelectComponent>
        </>
      )}
    </div>
  )
}

export default NarrationFormComponent
